// Browser file I/O for the studio. Uses the File System Access API; remembers the
// last-used file *per key* (e.g. per bundle) by persisting its handle in IndexedDB
// and reopening the picker in that location via `startIn`. The shared picker `id`
// is the fallback location when nothing is remembered. Falls back to a download /
// hidden file input where the API is unavailable (Firefox/Safari).

export const STUDIO_PICKER_ID = 'sigdex-studio';

const JSON_TYPES = [{ description: 'JSON', accept: { 'application/json': ['.json'] } }];

interface FsFileHandle {
  getFile: () => Promise<File>;
  createWritable: () => Promise<{
    write: (data: string) => Promise<void> | void;
    close: () => Promise<void> | void;
  }>;
}

interface FsWindow extends Window {
  showOpenFilePicker?: (opts: unknown) => Promise<FsFileHandle[]>;
  showSaveFilePicker?: (opts: unknown) => Promise<FsFileHandle>;
}

export function isFileAccessSupported(): boolean {
  const w = window as FsWindow;
  return typeof w.showSaveFilePicker === 'function' && typeof w.showOpenFilePicker === 'function';
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

// ─── Remembered directories (per key) ─────────────────────────────────────────
// File System Access handles are structured-cloneable, so they persist in
// IndexedDB across reloads. We store the last file handle saved/opened under a key
// and pass it as `startIn`, which opens the picker in that file's directory.
const DB_NAME = 'studio-fs';
const STORE = 'handles';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function dirKey(key: string): string {
  return `dir:${key}`;
}

async function loadHandle(key: string | undefined): Promise<FsFileHandle | undefined> {
  if (!key || typeof indexedDB === 'undefined') return undefined;
  try {
    const db = await openDb();
    return await new Promise<FsFileHandle | undefined>((resolve, reject) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(dirKey(key));
      req.onsuccess = () => resolve((req.result as FsFileHandle | undefined) ?? undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined; // best-effort: a missing/blocked DB just means no memory
  }
}

/** Persist `handle` as the remembered location for `key` (best-effort). */
export async function rememberHandle(key: string, handle: unknown): Promise<void> {
  if (!key || !handle || typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(handle, dirKey(key));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}

// ─── Open ─────────────────────────────────────────────────────────────────────
export interface OpenedFile {
  text: string;
  name: string;
  /** The picked file's handle, when the File System Access API was used. */
  handle?: unknown;
}

export async function pickFileToOpen(key?: string): Promise<OpenedFile | null> {
  const w = window as FsWindow;
  if (!isFileAccessSupported()) return pickViaInput();

  const remembered = await loadHandle(key);
  const open = async (startIn: FsFileHandle | undefined): Promise<OpenedFile> => {
    const opts: Record<string, unknown> = { id: STUDIO_PICKER_ID, types: JSON_TYPES };
    if (startIn) opts.startIn = startIn;
    const [handle] = await w.showOpenFilePicker!(opts);
    const file = await handle.getFile();
    if (key) await rememberHandle(key, handle);
    return { text: await file.text(), name: file.name, handle };
  };

  try {
    return await open(remembered);
  } catch (err) {
    if (isAbort(err)) return null;
    // A stale remembered handle can make the picker throw — retry once, clean.
    if (remembered) {
      try {
        return await open(undefined);
      } catch (retryErr) {
        if (isAbort(retryErr)) return null;
        throw retryErr;
      }
    }
    throw err;
  }
}

// ─── Save ─────────────────────────────────────────────────────────────────────
export async function saveFile(suggestedName: string, text: string, key?: string): Promise<void> {
  const w = window as FsWindow;
  if (!isFileAccessSupported()) {
    download(suggestedName, text);
    return;
  }

  const remembered = await loadHandle(key);
  const save = async (startIn: FsFileHandle | undefined): Promise<void> => {
    const opts: Record<string, unknown> = {
      id: STUDIO_PICKER_ID,
      suggestedName,
      types: JSON_TYPES,
    };
    if (startIn) opts.startIn = startIn;
    const handle = await w.showSaveFilePicker!(opts);
    const writable = await handle.createWritable();
    await writable.write(text);
    await writable.close();
    if (key) await rememberHandle(key, handle);
  };

  try {
    await save(remembered);
  } catch (err) {
    if (isAbort(err)) return;
    // A stale remembered handle can make the picker throw — retry once, clean.
    if (remembered) {
      try {
        await save(undefined);
      } catch (retryErr) {
        if (isAbort(retryErr)) return;
        throw retryErr;
      }
      return;
    }
    throw err;
  }
}

function download(filename: string, text: string) {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function pickViaInput(): Promise<OpenedFile | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      resolve({ text: await file.text(), name: file.name });
    };
    input.click();
  });
}
